import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  WindxTela,
  OpcaoRmv,
  TipoRodada,
  Patamar,
  Unidade,
} from 'helper/windx';

/**
 * Slice inicial do Windx — apenas estrutura (FE-WINDX-001).
 * Campos de filtros especificos serao adicionados em FE-WINDX-003 (PainelDeControle).
 *
 * Docs de referencia:
 * - docs/creaate_issues/ISSUES_GITLAB_FRONTEND.md (FE-WINDX-001)
 * - windx-docs: REQUISITOS_WINDX.md, TEMPLATE_ISSUES_WINDX.md
 *
 * Tipos de dominio (WindxTela, OpcaoRmv, TipoRodada, Patamar) vem de
 * `helper/windx.ts` — fonte unica (FE-WINDX-002).
 */

// Re-export dos tipos para manter compatibilidade com consumidores que ja
// importavam do reducer (ex: componentes e testes criados na FE-WINDX-001).
export type {
  WindxTela,
  OpcaoRmv,
  TipoRodada,
  Patamar,
  Unidade,
};

// Sub-tab dentro de WEOL-SH (FE-WINDX-050). Mapeada para `frequencia_temporal`
// nos requests: 'semi' -> 'sh', 'diario' -> 'd'. Existe como dimensao separada
// de `telaAtiva` para preservar prefs antigas (tipo='sh') sem migration BE.
export type WindxGranularidadeSH = 'semi' | 'diario';

interface WindxControlPanelState {
  telaAtiva: WindxTela;
  granularidadeSH: WindxGranularidadeSH;
  // WINDX-034: estudoAutomatico foi removido como estado independente.
  // Modo eh derivado: `estudoAutomatico = !dataSimulacao`. Selector
  // `selectWindxEstudoAutomatico` mantido para compat de consumidores.
  fatorRen: boolean;
  incluirSimulacoesAntigas: boolean;
  // dataSimulacao: campo legacy do datepicker antigo. Em FE-WINDX-043 v7
  // (multi-checkbox) ele NAO eh mais setado por simulacoes antigas — cada
  // simulacao ativa gera sua propria chamada paralela ao /chart/data/ no
  // hook useChartDataMergedSH com a data resolvida `hoje - dias_atras`.
  // Consumers existentes (SeletorSubsistemas, BotaoDownload, etc) que ainda
  // leem este campo veem `undefined` no fluxo novo — comportam-se como
  // Estudo Automatico, que e o esperado (sims antigas sao overlay grafico).
  dataSimulacao: string | undefined;
  // FE-WINDX-043 v7: ids das simulacoes antigas ATUALMENTE selecionadas
  // (checkbox multi). Limite 1..3 enforced no toggle action. Array vazio =
  // nenhuma sim ativa (Estudo Automatico apenas, se configurado).
  idsSimulacoesAntigasAtivas: number[];
  idsModelos: number[];
  // WINDX-030 (contrato v7.0): substitui `opcaoRmv: OpcaoRmv | undefined`.
  // Em WEOL-SH eh checkbox multi (1 ou 2 itens). Em WEOL-SM e Comparativo,
  // sempre [length=1] via Rmv apenasComRmv=true (regra ADR-015).
  opcoesRmv: OpcaoRmv[];
  // WINDX-056: pode ser undefined no boot (sem default de Tipo de Rodada).
  // `selectWindxFiltrosCompletos` exige `!!tipoRodada` antes de liberar a
  // consulta — no WEOL-SH e, desde o WEOL-SM (#99), no Semanal e no
  // Comparativo —, e o request omite o campo quando undefined. So e zerado
  // junto com a subdivisao (ver o comentario do `setTipoRodada`).
  tipoRodada: TipoRodada | undefined;
  patamar: Patamar;
  unidade: Unidade;
  idSubdivisaoEspacial: number | undefined;
  idsEntidades: number[];
  // Apenas-via-botao: filtros marcam pendente; clique em "Realizar Consulta"
  // incrementa o counter e os graficos observam ele via useLazyQuery.
  consultaPendente: boolean;
  triggerConsulta: number;
}

// FE-WINDX-043 v7: limite de simulacoes antigas selecionaveis ao mesmo
// tempo. Acima disso o grafico vira poluicao visual (2 sims x 11 modelos
// x 2 RMV = potencial 44+ curvas).
export const LIMITE_SIMULACOES_ANTIGAS_ATIVAS = 2;

// Sub-abas do WEOL-SM: Semanal ('sm') e Comparativo. Usam o mesmo painel-options
// ('sm') e as mesmas rodadas (WEOL-SM #99).
const ehTelaSM = (tela: WindxTela): boolean => tela === 'sm' || tela === 'comparativo';

const initialState: WindxControlPanelState = {
  telaAtiva: 'sh',
  granularidadeSH: 'semi',
  fatorRen: false,
  incluirSimulacoesAntigas: false,
  dataSimulacao: undefined,
  idsSimulacoesAntigasAtivas: [],
  idsModelos: [],
  // WINDX-056: WEOL-SH abre SEM default de RMV e Tipo de Rodada. Com os
  // antigos `['com_rmv']`/`'final'`, a 1a chamada de modelos-disponiveis
  // filtrava por essa combinacao e a ERA5_Climatologia (id=15, so existe
  // como preliminar+sem_rmv) aparecia sempre "(sem dados)". Vazio => o boot
  // chama modelos-disponiveis sem tipo_rodada/com_rmv (BE = "qualquer") e a
  // climatologia aparece habilitada. SM/Comparativo recebem ['com_rmv']
  // explicitamente em setTelaAtiva/resetWindxControlPanel (ADR-015).
  opcoesRmv: [],
  tipoRodada: undefined,
  patamar: 'media',
  unidade: 'mw',
  idSubdivisaoEspacial: undefined,
  idsEntidades: [],
  consultaPendente: false,
  triggerConsulta: 0,
};

export const windxControlPanelSlice = createSlice({
  name: 'windxControlPanel',
  initialState,
  reducers: {
    setTelaAtiva: (state, action: PayloadAction<WindxTela>) => {
      // Troca de aba: reseta TODOS os filtros (mesma logica do "Limpar
      // filtros") preservando apenas a nova `telaAtiva` — e, entre Semanal e
      // Comparativo, o Tipo de Rodada (WEOL-SM #99, abaixo). Evita state leak
      // entre WEOL-SH e WEOL-SM (bug do PO 2026-05-18 #3): se o usuario
      // selecionava modelos/subdivisao/RMV em uma aba e trocava para a
      // outra, o estado contaminava o painel da nova aba e podia gerar
      // request com payload misturado.
      //
      // Bug do PO 2026-05-19: ao entrar em Comparativo ou Semanal,
      // pre-selecionar ONS Oficial (id_modelo=13) como default.
      //
      // WINDX-028 (PO 2026-05-21): reset forte tambem ao trocar ENTRE
      // sub-abas SM (Semanal <-> Comparativo). Antes, trocar entre sub-abas
      // SM caia no caminho "telaAtiva igual" (era 'sm' OU 'comparativo';
      // novo payload eh outra string) — entao na verdade ja resetava.
      // Mas como o seletor de Modelos agora eh radio com default Oficial,
      // garantimos que qualquer transicao para 'sm' ou 'comparativo'
      // sempre forca idsModelos=[13], mesmo se a tela anterior ja era SM.
      if (state.telaAtiva !== action.payload) {
        const next = {
          ...initialState,
          telaAtiva: action.payload,
        };
        // WEOL-SM (#99): Semanal e Comparativo sao a mesma tela (mesmo
        // painel-options 'sm', mesmas rodadas preliminar/final), entao a troca
        // ENTRE as duas sub-abas preserva o Tipo de Rodada: o usuario nao escolhe
        // de novo e, com a flag do BE desligada, o 'final' que o
        // useControlPanelRules assumiu continua valendo. O resto segue o reset
        // forte (WINDX-028), inclusive a subdivisao. SH <-> SM zera o tipo junto
        // com tudo: outro painel, outras rodadas.
        if (ehTelaSM(state.telaAtiva) && ehTelaSM(action.payload)) {
          next.tipoRodada = state.tipoRodada;
        }
        // WINDX-056: SM/Comparativo sempre com_rmv (ADR-015), setado de forma
        // sincrona (initialState abre vazio para o WEOL-SH).
        //
        // WINDX-065: o Semanal deixa de ser radio+default. Agora:
        // - Comparativo (radio, single): COLAPSA — ao vir do Semanal com >=1
        //   modelo marcado, preserva o 1o selecionado; senao default ONS
        //   Oficial (id=13).
        // - Semanal (checkbox multi-modelo): SEM default — abre vazio
        //   (empty-state "selecione ao menos um modelo").
        if (action.payload === 'comparativo') {
          const veioDeSemanal = state.telaAtiva === 'sm' && state.idsModelos.length > 0;
          next.idsModelos = veioDeSemanal ? [state.idsModelos[0]] : [13];
          next.opcoesRmv = ['com_rmv'];
        } else if (action.payload === 'sm') {
          next.idsModelos = [];
          next.opcoesRmv = ['com_rmv'];
        }
        return next;
      }
      state.telaAtiva = action.payload;
      return state;
    },
    // Troca de sub-tab dentro de WEOL-SH (Semi-horário <-> Diário).
    //
    // Hotfix (sugestao do Andre, 2026-10-26): MANTEM os filtros e entidades
    // selecionados — antes resetava tudo como o setTelaAtiva. As duas
    // sub-abas consultam o mesmo /chart/data/ mudando so a
    // `frequencia_temporal`, entao a selecao continua valida.
    //
    // Com os filtros completos a consulta dispara sozinha (incrementa o
    // trigger) — o useChartDataMergedSH le do cache do RTK Query quando so a
    // granularidade mudou, entao voltar para a sub-aba ja consultada reexibe
    // o resultado sem novo request. Incompletos: marca `consultaPendente`
    // para o grafico nao exibir dados da granularidade anterior.
    setGranularidadeSH: (state, action: PayloadAction<WindxGranularidadeSH>) => {
      if (state.granularidadeSH !== action.payload) {
        state.granularidadeSH = action.payload;
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        if (selectWindxFiltrosCompletos({ windxControlPanel: state })) {
          state.triggerConsulta += 1;
          state.consultaPendente = false;
        } else {
          state.consultaPendente = true;
        }
      }
    },
    setFatorRen: (state, action: PayloadAction<boolean>) => {
      state.fatorRen = action.payload;
      state.consultaPendente = true;
    },
    setIncluirSimulacoesAntigas: (state, action: PayloadAction<boolean>) => {
      state.incluirSimulacoesAntigas = action.payload;
      state.consultaPendente = true;
    },
    setDataSimulacao: (state, action: PayloadAction<string | undefined>) => {
      // Action legacy do datepicker antigo do <SimulacoesAntigas>.
      // FE-WINDX-043 v7: nao eh mais usada pelo novo fluxo de simulacoes
      // antigas multi-checkbox (cada sim faz sua propria chamada paralela
      // ao /chart/data/ via useChartDataMergedSH). Mantida para nao quebrar
      // potenciais consumers externos. Quando setado, limpa lista de
      // ativas para evitar estado inconsistente.
      //
      // `triggerConsulta` NAO eh zerado aqui (monotonico): hooks de fetch
      // mantem `lastTrigger` em useRef que sobrevive ao Redux. Regredir o
      // counter cria janela onde realizarConsulta produz triggerConsulta
      // <= lastTrigger e o guard bloqueia o request (precisa de 2 cliques).
      if (state.dataSimulacao !== action.payload) {
        state.idsModelos = [];
      }
      state.dataSimulacao = action.payload;
      state.idsSimulacoesAntigasAtivas = [];
      state.consultaPendente = true;
    },
    // FE-WINDX-043 v7: toggle de simulacao antiga (checkbox click).
    // Se o id ja esta no array, REMOVE. Caso contrario ADICIONA — desde
    // que respeite o LIMITE_SIMULACOES_ANTIGAS_ATIVAS (2). Tentativa de
    // adicionar a 3a eh ignorada silenciosamente; UI bloqueia visualmente.
    toggleSimulacaoAntigaAtiva: (state, action: PayloadAction<number>) => {
      const id = action.payload;
      const ativas = state.idsSimulacoesAntigasAtivas;
      const idx = ativas.indexOf(id);
      if (idx >= 0) {
        // Desmarcar
        state.idsSimulacoesAntigasAtivas = ativas.filter((x) => x !== id);
      } else if (ativas.length < LIMITE_SIMULACOES_ANTIGAS_ATIVAS) {
        // Marcar (dentro do limite)
        state.idsSimulacoesAntigasAtivas = [...ativas, id];
      }
      // Acima do limite: no-op (UI deve impedir o click). Nao altera
      // consultaPendente nesse caso para nao gerar trigger fantasma.
      if (idx >= 0 || ativas.length < LIMITE_SIMULACOES_ANTIGAS_ATIVAS) {
        state.consultaPendente = true;
      }
    },
    // Util para forcar limpeza (ex: delete de simulacao ativa, troca de tela).
    limparSimulacoesAntigasAtivas: (state) => {
      if (state.idsSimulacoesAntigasAtivas.length > 0) {
        state.idsSimulacoesAntigasAtivas = [];
        state.consultaPendente = true;
      }
    },
    // Set bulk. Respeita o limite via slice — defesa contra prefs antigas
    // salvas com mais ids do que o limite atual permite.
    setIdsSimulacoesAntigasAtivas: (state, action: PayloadAction<number[]>) => {
      state.idsSimulacoesAntigasAtivas = action.payload.slice(
        0,
        LIMITE_SIMULACOES_ANTIGAS_ATIVAS,
      );
      state.consultaPendente = true;
    },
    setIdsModelos: (state, action: PayloadAction<number[]>) => {
      state.idsModelos = action.payload;
      state.consultaPendente = true;
    },
    setOpcoesRmv: (state, action: PayloadAction<OpcaoRmv[]>) => {
      // WINDX-030: substitui setOpcaoRmv. Min 1 item enforced no componente
      // Rmv (tentativa de desmarcar a ultima eh ignorada na UI).
      state.opcoesRmv = action.payload;
      state.consultaPendente = true;
    },
    // WEOL-SM (#99): tambem e despachado pelo useControlPanelRules, que assume
    // 'final' no Semanal/Comparativo quando o painel-options vem sem
    // `tipos_rodada` (flag do BE desligada). Marcar `consultaPendente` ai nao
    // vira um "Filtros alterados" falso: o hook so age com o tipo vazio, e o tipo
    // SO e zerado junto com a subdivisao (setTelaAtiva SH <-> SM,
    // resetWindxControlPanel, sairDoWindx), entao o hook repoe o 'final' logo
    // depois de uma dessas transicoes, com a subdivisao ainda vazia. Sem
    // subdivisao, os consumidores (GraficoSM, TabelaComparativoSM, BarraConsulta)
    // mostram o aviso de filtros incompletos antes de olhar o pendente. A regra e
    // de transicao, nao de estado: com a etapa Tipo de Rodada, o usuario pode
    // escolher a subdivisao antes do tipo. Nao zere o tipo sozinho: o teste
    // "nenhuma action zera o Tipo de Rodada mantendo a subdivisao" guarda isso.
    setTipoRodada: (state, action: PayloadAction<TipoRodada>) => {
      state.tipoRodada = action.payload;
      state.consultaPendente = true;
    },
    setPatamar: (state, action: PayloadAction<Patamar>) => {
      state.patamar = action.payload;
      state.consultaPendente = true;
    },
    setUnidade: (state, action: PayloadAction<Unidade>) => {
      state.unidade = action.payload;
      state.consultaPendente = true;
    },
    setIdSubdivisaoEspacial: (state, action: PayloadAction<number | undefined>) => {
      state.idSubdivisaoEspacial = action.payload;
      state.idsEntidades = [];
      state.consultaPendente = true;
    },
    setIdsEntidades: (state, action: PayloadAction<number[]>) => {
      state.idsEntidades = action.payload;
      state.consultaPendente = true;
    },
    realizarConsulta: (state) => {
      state.triggerConsulta += 1;
      state.consultaPendente = false;
    },
    resetWindxControlPanel: (state) => {
      // Bug do PO 2026-05-19: clicar "Limpar filtros" na tela Comparativo
      // estava voltando `telaAtiva` para 'sh' (initialState default), e
      // o painel "Preferencias Salvas" passava a listar preferencias do
      // WEOL-SH. Mesma logica do setTelaAtiva: zera tudo mas preserva a
      // tela atual e aplica default ONS Oficial em Comparativo e Semanal.
      //
      // Bug do PO 2026-05-26 (WINDX-bug-limpar-filtros): `triggerConsulta`
      // tambem precisa ser preservado. Ele eh counter monotonico observado
      // por hooks de fetch via `useRef` (lastTrigger) que sobrevive ao
      // reset do Redux. Voltar para 0 cria janela onde o primeiro clique
      // em Realizar Consulta apos Limpar produz triggerConsulta=1 <=
      // lastTrigger=1 e o guard bloqueia o request — usuario precisa
      // clicar 2x. Mantendo o counter monotonico, o proximo incremento
      // sempre supera lastTrigger.
      const { telaAtiva: tela, triggerConsulta } = state;
      const next = {
        ...initialState,
        telaAtiva: tela,
        triggerConsulta,
      };
      // WINDX-056: SM/Comparativo sempre com_rmv (ADR-015), setado de forma
      // sincrona (initialState agora abre vazio para o WEOL-SH).
      // WINDX-065: Comparativo (radio) restaura o default ONS Oficial; o
      // Semanal (checkbox multi-modelo) volta ao empty-state (sem default).
      if (tela === 'comparativo') {
        next.idsModelos = [13];
        next.opcoesRmv = ['com_rmv'];
      } else if (tela === 'sm') {
        next.opcoesRmv = ['com_rmv'];
      }
      return next;
    },
    // Saida do modulo Windx (o WindxWrapper desmonta: outra tela, logout).
    //
    // Hotfix (Palin, 2026-10-27): a consulta anterior continuava na tela ao
    // voltar para o Windx, porque o slice nao e persistido mas tambem nao era
    // limpo ao sair. Volta TUDO ao initialState, inclusive o triggerConsulta:
    // diferente do resetWindxControlPanel, aqui os hooks de fetch (com o
    // `lastTrigger` em useRef) desmontam junto e voltam do zero, entao zerar
    // o counter nao bloqueia o proximo clique — e preservar faria a consulta
    // disparar sozinha assim que os filtros fossem preenchidos de novo.
    sairDoWindx: () => initialState,
  },
});

export const selectWindxTelaAtiva = (state: any): WindxTela => (
  state.windxControlPanel.telaAtiva
);
export const selectWindxGranularidadeSH = (state: any): WindxGranularidadeSH => (
  state.windxControlPanel.granularidadeSH
);
// WINDX-034: derivado de dataSimulacao. Quando usuario nao escolheu data no
// accordion "Incluir Simulacoes Antigas", entendemos como Estudo Automatico
// (request omite `data_simulacao`, BE usa MAX(data_rodada) ou hoje).
export const selectWindxEstudoAutomatico = (state: any): boolean => (
  !state.windxControlPanel.dataSimulacao
);
export const selectWindxFatorRen = (state: any): boolean => (
  state.windxControlPanel.fatorRen
);
export const selectWindxIncluirSimulacoesAntigas = (state: any): boolean => (
  state.windxControlPanel.incluirSimulacoesAntigas
);
export const selectWindxDataSimulacao = (state: any): string | undefined => (
  state.windxControlPanel.dataSimulacao
);
// FE-WINDX-043 v7: ids das simulacoes antigas atualmente selecionadas
// (checkbox multi). Array vazio = nenhuma ativa.
export const selectWindxIdsSimulacoesAntigasAtivas = (state: any): number[] => (
  state.windxControlPanel.idsSimulacoesAntigasAtivas
);
export const selectWindxIdsModelos = (state: any): number[] => (
  state.windxControlPanel.idsModelos
);
export const selectWindxOpcoesRmv = (state: any): OpcaoRmv[] => (
  state.windxControlPanel.opcoesRmv
);
export const selectWindxTipoRodada = (state: any): TipoRodada | undefined => (
  state.windxControlPanel.tipoRodada
);
export const selectWindxPatamar = (state: any): Patamar => (
  state.windxControlPanel.patamar
);
export const selectWindxUnidade = (state: any): Unidade => (
  state.windxControlPanel.unidade
);
export const selectWindxIdSubdivisaoEspacial = (state: any): number | undefined => (
  state.windxControlPanel.idSubdivisaoEspacial
);
export const selectWindxIdsEntidades = (state: any): number[] => (
  state.windxControlPanel.idsEntidades
);
export const selectWindxConsultaPendente = (state: any): boolean => (
  state.windxControlPanel.consultaPendente
);
export const selectWindxTriggerConsulta = (state: any): number => (
  state.windxControlPanel.triggerConsulta
);

/**
 * Selector derivado: computa period SH (dataInicio, dataFim) a partir do
 * horizonte do modelo selecionado. NAO e armazenado em Redux — e derivado
 * em tempo de selecao (Task 1, FE-WINDX-013).
 *
 * Inputs: idsModelos, painel_options (obtido via RTK Query em outro selector)
 * Retorna: { dataInicio: 'YYYY-MM-DD', dataFim: 'YYYY-MM-DD' }
 *
 * Implementacao: chama `derivarPeriodoEixoX()` do helper; o resultado varia
 * diariamente quando `dataRodada` (hoje) muda.
 */
export const selectWindxPeriodoEixoX = (): { dataInicio: string; dataFim: string } => {
  // Fallback simples (14 dias) ate que Task 1 helper esteja integrado.
  // TODO: integrar derivarPeriodoEixoX aqui quando painel_options estiver
  // disponivel como selector (RTK Query).
  const hoje = new Date();
  const fim = new Date(hoje);
  fim.setDate(fim.getDate() + 14);
  const ymd = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { dataInicio: ymd(hoje), dataFim: ymd(fim) };
};

/**
 * Filtros obrigatorios prontos EXCETO a lista de entidades.
 *
 * Util para componentes que mantem buffer local de entidades (ex.: o
 * SeletorEntidades, que so commita ao Redux quando o usuario clica OK).
 * Esses componentes combinam este selector com seu buffer local para
 * decidir se podem auto-disparar `realizarConsulta()`.
 */
export const selectWindxFiltrosObrigatoriosExcetoEntidades = (state: any): boolean => {
  const s = state.windxControlPanel;
  const subdivOk = typeof s.idSubdivisaoEspacial === 'number';
  const modelosOk = Array.isArray(s.idsModelos) && s.idsModelos.length > 0;
  // FE-WINDX-043 v7: em WEOL-SH a consulta pode ser disparada por
  //   (a) Estudo Automatico configurado (modelos + RMV + tipo_rodada), OU
  //   (b) >=1 Simulacao Antiga selecionada (cada uma tem filtros proprios), OU
  //   (c) AMBOS — series somam no grafico.
  // Subdivisao + entidades continuam obrigatorios em qualquer caso.
  if (s.telaAtiva === 'sh') {
    const rmvOk = Array.isArray(s.opcoesRmv) && s.opcoesRmv.length > 0;
    const estudoAutomaticoOk = modelosOk && rmvOk && !!s.tipoRodada;
    const simAntigaOk = (
      Array.isArray(s.idsSimulacoesAntigasAtivas)
      && s.idsSimulacoesAntigasAtivas.length > 0
    );
    return subdivOk && (estudoAutomaticoOk || simAntigaOk);
  }
  // sm (Semanal e Comparativo): patamar tem default 'media'. WEOL-SM (#99): o
  // tipo de rodada tambem e obrigatorio. A trava e incondicional porque o
  // reducer nao ve o painel-options: com a flag do BE desligada (painel sem
  // `tipos_rodada`), o useControlPanelRules assume 'final' — o default do BE —
  // e a consulta segue liberando como antes.
  return subdivOk && modelosOk && !!s.patamar && !!s.tipoRodada;
};

/**
 * Filtros obrigatorios completos para realizar a consulta.
 *
 * Regra confirmada com a PO (2026-05-13):
 *   - SH: subdivisao + entidades + modelos + RMV + tipo_rodada
 *         + (estudo_automatico OU data_simulacao)
 *   - SM: subdivisao + entidades + modelos + patamar
 *         + tipo_rodada (WEOL-SM #99; Semanal e Comparativo)
 *
 * O botao "Realizar Consulta" e os auto-triggers (toggle Geracao/FC e OK do
 * seletor de entidades) so devem disparar quando este selector retorna true.
 */
export const selectWindxFiltrosCompletos = (state: any): boolean => (
  selectWindxFiltrosObrigatoriosExcetoEntidades(state)
  && Array.isArray(state.windxControlPanel.idsEntidades)
  && state.windxControlPanel.idsEntidades.length > 0
);

export const {
  setTelaAtiva,
  setGranularidadeSH,
  setFatorRen,
  setIncluirSimulacoesAntigas,
  setDataSimulacao,
  toggleSimulacaoAntigaAtiva,
  limparSimulacoesAntigasAtivas,
  setIdsSimulacoesAntigasAtivas,
  setIdsModelos,
  setOpcoesRmv,
  setTipoRodada,
  setPatamar,
  setUnidade,
  setIdSubdivisaoEspacial,
  setIdsEntidades,
  realizarConsulta,
  resetWindxControlPanel,
  sairDoWindx,
} = windxControlPanelSlice.actions;

export default windxControlPanelSlice.reducer;
